import { PoolVersionTypeMapping } from "@antefinance/ante-sdk/dist/ante";
import { BigNumber, BigNumberish, BytesLike } from "ethers";
import { SendTransaction } from "utils/antepools/fetchAntePoolsPublic";
import { ChainId, TokenDetails } from "utils/constants";
import { TokenPrice } from "hooks/useFetchTokenPrice";
export * from "./Rating";
export * from "./TrustScoreStats";
export * from "./ProjectVersion";
export * from "./SourceCode";

export type Address = string;

export type AnteNumber = {
  number: number;
  bn: BigNumber;
};

export type PoolBalancesMap = {
  [key: string]: PoolBalances;
};

export type PoolBalances = {
  stakerBalances: Record<string, BigNumber>;
  challengerBalances: Record<string, BigNumber>;
};

export enum PoolPositions {
  None,
  Staked,
  Challenged,
}

export interface Avl {
  timestamp: number;
  value: number;
}

export interface PoolSideInfoDB {
  totalAmount: string;
  decayMultiplier: string;
  numUsers: string;
}

export interface PoolSideInfo {
  totalAmount: AnteNumber;
  decayMultiplier: AnteNumber;
  numUsers: number;
}

export interface TestAuthor {
  address: string;
  ensName: string;
}

export interface StakerInfo {
  address?: string;
  isVerified?: boolean;
  displayName?: string;
  ensName?: string;
  amount?: string;
  timestamp?: number;
}

export interface SupporterInfo extends StakerInfo {
  unlockTimestamp?: number;
}

export interface ChallengerInfo extends StakerInfo {
  claimableShares?: number;
}

export interface ChallengerUserInfo {
  startAmount: AnteNumber;
  lastStakedTimestamp: number;
  claimableShares: AnteNumber;
  claimableSharesStartMultiplier: AnteNumber;
}

export type PoolDB = {
  _id?: string;
  antePoolAddress?: string;
  anteTestName?: string;
  version?: PoolVersionType;
  chainId?: ChainId;
  stakingInfo?: PoolSideInfoDB;
  challengerInfo?: PoolSideInfoDB;
  testDescription?: string;
  anteTestAddress?: string;
  timestamp?: number;
  testAuthor?: TestAuthor;
  pendingWithdrawAmount?: string;
  pendingFailure?: boolean;
  numTimesVerified?: string;
  lastVerifiedBlock?: string;
  verifier?: string;
  testedContracts?: Array<string>;
  totalStaked?: Array<Avl>;
  totalChallengerStaked?: Array<Avl>;
  testAuthorReward?: Array<Avl>;
  teamVerified?: boolean;
  writtenByTeam?: boolean;
  protocolStaked?: boolean;
  isCommunityPool?: boolean;
  decayRate?: number;
  challengerPayoutRatio?: number;
  testAuthorRewardRate?: number;
  tokenAddress?: string;
  minChallengerStake?: string;
  minSupporterStake?: string;
  verifiedStakers?: SupporterInfo[];
  verifiedChallengers?: ChallengerInfo[];
  stakers?: SupporterInfo[];
  challengers?: ChallengerInfo[];
  lastVerifiedTimestamp?: number;
};

/**
 * Shape of a single AntePool as returned by the REST API at GET /antepools.
 * Use fetchAntePoolsFromAPI to load data; then format with formatPool/formatPools.
 */
export type AntePoolFromAPI = PoolDB;

export type BasePool = Omit<
  PoolDB,
  | "stakingInfo"
  | "challengerInfo"
  | "pendingWithdrawAmount"
  | "numTimesVerified"
  | "lastVerifiedBlock"
  | "minSupporterStake"
  | "minChallengerStake"
> & {
  // Formatted & on-chain fields
  stakingInfo?: PoolSideInfo;
  challengerInfo?: PoolSideInfo;
  minSupporterStake?: AnteNumber;
  minChallengerStake?: AnteNumber;
  pendingWithdrawAmount?: AnteNumber;
  totalPendingWithdraw?: AnteNumber;
  numTimesVerified?: number;
  lastVerifiedBlock?: number;
  token?: TokenDetails;

  // On-chain fields
  stakedBalance?: AnteNumber;
  challengedBalance?: AnteNumber;
  pendingWithdrawAllowedTime?: number;
  challengerPayout?: AnteNumber;
  stakedStartAmount?: AnteNumber;
  challengedStartAmount?: AnteNumber;
  verifierBounty?: AnteNumber;
  checkTestAllowedBlock?: number;

  // write functions
  sendTransaction?: SendTransaction;
};

export type PoolComputedFields = {
  tvl?: AnteNumber;
  trustScore?: number;
  isTvlGteThreshold?: boolean;
  stakedAndPendingWithdraw?: AnteNumber;
  poolPosition?: PoolPositions;
};

export type PoolV05 = BasePool &
  PoolComputedFields & {
    // write functions
    claim?: () => Promise<any>;
    stake?: (value: BigNumberish) => Promise<any>;
    challenge?: (value: BigNumberish) => Promise<any>;
    withdrawChallenge?: () => Promise<any>;
    checkTest?: () => Promise<any>;
    unstakeAll?: (isChallenger: boolean) => Promise<any>;
    withdrawStake?: () => Promise<any>;
    cancelPendingWithdraw?: () => Promise<any>;
  };

export type PoolV06 = BasePool &
  PoolComputedFields & {
    // Computed fields
    userChallengerInfo?: ChallengerUserInfo;
    unlockTimestamp?: number;
    totalChallengerEligibleBalance?: AnteNumber;
    challengerStartDecayMultiplier?: BigNumber;

    // write functions
    claim?: () => Promise<any>;
    claimReward?: () => Promise<any>;
    stake?: (value: BigNumberish, commitTime: BigNumberish) => Promise<any>;
    extendStakeLock?: (additionalTime: BigNumberish) => Promise<any>;
    registerChallenge?: (value: BigNumberish) => Promise<any>;
    confirmChallenge?: () => Promise<any>;
    withdrawChallenge?: () => Promise<any>;
    checkTest?: () => Promise<any>;
    checkTestWithState?: (state: BytesLike) => Promise<any>;
    unstakeAll?: (isChallenger: boolean) => Promise<any>;
    withdrawStake?: () => Promise<any>;
    cancelPendingWithdraw?: () => Promise<any>;
  };
export type Pool = PoolV05 & PoolV06;
export type PoolVersionType = keyof PoolVersionTypeMapping;

export type TokenAmount = {
  token: string;
  amount: string;
};

export type AnteTestDB = {
  _id?: string;
  name?: string;
  chainId?: string;
  description?: string;
  address?: string;
  author?: TestAuthor;
  protocolName?: string;
  testedContracts?: Array<string>;
  isTeamVerified?: boolean;
  isWrittenByTeam?: boolean;
  hasProtocolStaked?: boolean;
  hasFailed?: boolean;
  verifier?: string;
  tvl?: TokenAmount[];
  tvlInUsd?: number;
  totalStaked?: TokenAmount[];
  totalChallenged?: TokenAmount[];
  stakedByProtocol?: TokenAmount[];
  pendingWithdraw?: TokenAmount[];
  lastVerifiedBlock?: number;
  lastVerifiedTimestamp?: number;
  totalPaidOutInUsd?: number;
  testStateTypes?: string;
  testStateNames?: string;
  poolVersions?: string[];
};

export type TokenValue = {
  tokens: BigNumber;
  usd: number;
};

export type AnteTestUserPosition = {
  totalStakedInUsd: number;
  totalChallengedInUsd: number;
  totalPendingWithdrawInUsd: number;
  breakdown: Record<
    Address,
    {
      staked: TokenValue;
      challenged: TokenValue;
      pendingWithdraw: TokenValue;
    }
  >;
};
export type AnteTest = Omit<
  AnteTestDB,
  | "tvl"
  | "totalStaked"
  | "totalChallenged"
  | "stakedByProtocol"
  | "pendingWithdraw"
> & {
  getTrustScore?: (prices: TokenPrice) => number;

  timestamp?: number;
  pools?: Pool[];
  userPosition?: AnteTestUserPosition;
  hasOnlyLegacyPools: boolean;

  tvl?: Record<Address, AnteNumber>;
  getTvlInUsd?: (prices: TokenPrice) => number;
  totalStaked?: Record<Address, AnteNumber>;
  getTotalStakedInUsd?: (prices: TokenPrice) => number;
  totalChallenged?: Record<Address, AnteNumber>;
  stakedByProtocol?: Record<Address, AnteNumber>;
  pendingWithdraw?: Record<Address, AnteNumber>;
  getPendingWithdrawInUsd?: (prices: TokenPrice) => number;
};

export type CacheKeys = {
  all: () => any[];
  filtered: (filters?: Record<string, any>) => any[];
  single: (...args: any) => any[];
};

export type SocialMediaType = "discord" | "telegram" | "twitter" | "facebook";

export type SocialMedia = {
  type: SocialMediaType;
  url: string;
};

export type ChainTokenAmount = TokenAmount & {
  chainId: string;
};

export interface ProjectDB {
  _id?: string;
  name?: string;
  slug?: string;
  category?: string;
  chainIds?: ChainId[];
  alternateNames?: string[];
  isVerified?: boolean;
  logo?: string;
  description?: string;
  websiteUrl?: string;
  socialMedia?: SocialMedia[];
  verifiedAddresses?: string[];
  tvl?: ChainTokenAmount[];
  tvlInUsd?: number;
  totalStaked?: ChainTokenAmount[];
  totalChallenged?: ChainTokenAmount[];
  stakedByProtocol?: ChainTokenAmount[];
  preFailureTvl?: ChainTokenAmount[];
  pendingWithdraw?: ChainTokenAmount[];
}

export type Project = Omit<
  ProjectDB,
  | "tvl"
  | "totalStaked"
  | "totalChallenged"
  | "stakedByProtocol"
  | "preFailureTvl"
  | "pendingWithdraw"
> & {
  tvl?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
  tvlInUsd?: number;
  totalStaked?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
  totalChallenged?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
  stakedByProtocol?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
  preFailureTvl?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
  pendingWithdraw?: PartialRecord<ChainId, Record<Address, AnteNumber>>;
};

export type ModalProps = {
  isShown?: boolean;
  hide?: () => void;
};

export type CommunityScoreDB = {
  contributor: string;
  communityPRs: number;
  communityScore: number;
  address: string;
};

export type CommunityScore = CommunityScoreDB;

export type PollInfoDB = {
  tvl?: string;
};

export type MoonPlungePollInfo = {
  tvl?: number;
};

export type StableCoinPollInfo = {
  peg?: boolean;
};

export type PollKey = "moon-or-plunge" | "stablecoin-peg";
export type PollType = {
  _id?: string;
  name?: string;
  pollKey?: PollKey;
  description?: string;
  durationSeconds?: number;
  recurrences?: number;
};

export type MoonPlungePollOptions = {
  delta: number;
};

export type StableCoinPollOptions = {
  delta: number;
};
export type VoteType = "option" | "binary";
export type VoteValue = string;

export type KeyValue<T> = {
  key: string;
  value: T;
};

export type PollConfigDB = {
  voteType?: VoteType;
  voteOptions?: KeyValue<string>[];
  pollOptions?: KeyValue<string>[];
};
export type PollConfig<O extends {}> = Omit<
  PollConfigDB,
  "voteOptions" | "pollOptions"
> & {
  voteOptions?: Map<VoteValue, string>;
  pollOptions?: O;
};

export type AntePollDB = {
  _id?: string;
  project?: ProjectDB;
  pollType?: PollType;
  configuration?: PollConfigDB;
  startDate?: string;
  endDate?: string;
  initialInfo?: PollInfoDB;
  currentInfo?: PollInfoDB;
  votesIndex?: KeyValue<number>[];
  finalInfo?: PollInfoDB;
};

export type AntePoll<C extends {}, I extends {}> = Omit<
  AntePollDB,
  | "project"
  | "configuration"
  | "startDate"
  | "endDate"
  | "votesIndex"
  | "initialInfo"
  | "currentInfo"
  | "finalInfo"
> & {
  startDate?: Date;
  endDate?: Date;
  project?: Project;
  configuration?: PollConfig<C>;
  votesIndex?: Record<VoteValue, number>;
  initialInfo?: I;
  currentInfo?: I;
  finalInfo?: I;
};

export type PollConfigOptions = any;
export type PollConfigInfo = any;

export type MoonPlungePoll = AntePoll<
  MoonPlungePollOptions,
  MoonPlungePollInfo
> & {
  votes: {
    moon: number;
    crab: number;
    plunge: number;
  };
};

export type StableCoinPoll = AntePoll<
  StableCoinPollOptions,
  StableCoinPollInfo
> & {
  votes: {
    yes: number;
    no: number;
  };
};

export type AntePollVoteDB = {
  _id?: string;
  user?: AnteUserDB;
  poll?: AntePollDB;
  vote?: VoteValue;
  voteDate?: Date;
};

export type PollVoteMutation = {
  user?: RelationInput;
  poll?: RelationInput;
  vote?: VoteValue;
  voteDate?: Date;
};

export type AntePollVote = AntePollVoteDB;

export type RelationInput = {
  link: string;
};

export type AnteUserDB = {
  _id?: string;
  address?: string;
  deviceUUID?: string;
};

export type AnteUser = AnteUserDB;

export type RealmUserCustomData = {
  _id?: string;
  deviceUUID?: string;
  address?: string;
  metadata?: {
    addresses?: Record<string, string>;
  };
};

export type TestType = "nft-balance" | "token-balance";
export const TEST_TYPES = ["nft-balance", "token-balance"];

export type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

export type NFT = {
  _id?: string;
  address?: string;
  chainId?: string;
  name?: string;
  symbol?: string;
};

export type Token = {
  _id?: string;
  address?: string;
  chainId?: string;
  name?: string;
  symbol?: string;
  decimals?: string;
};
